import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@verantos-dev/app-state-base';
import {
  addResearchAssistantMessages,
  COHORT_ACTIONS,
  selectResearchAssistantModel,
} from '@verantos-dev/app-state-research-assistant';
import { useAuth } from '@verantos-dev/core-auth';
import { getCohortsPath } from '@verantos-dev/core-constants';
import { emitUserSaveEvent } from '@verantos-dev/feature-research-assistant';
import {
  CohortStatusLegacy,
  ResearchAssistantMessageEnumLegacy,
} from '@verantos-dev/types-cohorts';

import {
  CohortBuilderActionDialogType,
  useCohortBuilderModalContext,
} from '../../contexts/CohortBuilderModalContext';
import { useCohortBuilderSaveQuery } from '../../hooks/common/useCohortBuilderSaveQuery';
import { getCohortEditModalTitle } from '../../utils/getCohortEditModalTitle';

export const useSaveCohortDefinitionModal = () => {
  const [countDown, setCountDown] = useState(3);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const researchAssistantModel = useAppSelector(selectResearchAssistantModel);
  const { closeDialog } = useCohortBuilderModalContext();

  const onClose = () => {
    closeDialog();
  };

  const {
    projectId,
    cohortVersionId,
    cohortId,
    cohortData,
    isInitialLoading,
    initialLoadingError,
    isSubmitting,
    submitVersion,
    submittingError,
    isSubmitted,
    isSubmitSuccess,
    isTranslated,
  } = useCohortBuilderSaveQuery();

  const { user } = useAuth();

  const redirectToCohortList = useCallback(() => {
    projectId && navigate(getCohortsPath(projectId));
  }, [navigate, projectId]);

  const onSubmit = useCallback(() => {
    if (projectId && cohortId && cohortVersionId) {
      submitVersion();
      emitUserSaveEvent({
        model: researchAssistantModel,
        cohortVersionId,
        cohortId,
        projectId,
        user,
        cohortAction: COHORT_ACTIONS.SAVE,
      });
      dispatch(
        addResearchAssistantMessages([
          {
            message: intl.formatMessage({
              id: 'projects.cohortBuilderPage.researchAssistant.savedInfoMessage',
            }),
            timestamp: new Date().toISOString(),
            currentUser: 'user',
            isCurrentUser: true,
            type: ResearchAssistantMessageEnumLegacy.SAVE,
          },
        ])
      );
    }
  }, [
    cohortId,
    cohortVersionId,
    dispatch,
    intl,
    projectId,
    researchAssistantModel,
    submitVersion,
    user,
  ]);

  useEffect(() => {
    if (
      isTranslated &&
      !isInitialLoading &&
      cohortData?.data.latestStatus &&
      [CohortStatusLegacy.DRAFT, CohortStatusLegacy.ERROR].includes(
        cohortData?.data.latestStatus
      ) &&
      projectId &&
      cohortId &&
      !isSubmitted
    ) {
      onSubmit();
    }
  }, [
    cohortData?.data.latestStatus,
    cohortId,
    onSubmit,
    projectId,
    isSubmitted,
    isInitialLoading,
    isTranslated,
  ]);

  useEffect(() => {
    if (isSubmitSuccess && countDown > 0) {
      const interval = setInterval(() => {
        setCountDown((prevCount) => prevCount - 1);

        if (countDown === 1) {
          clearInterval(interval);
          closeDialog();
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [closeDialog, countDown, isSubmitSuccess, redirectToCohortList]);

  return {
    intl,
    title: getCohortEditModalTitle(
      intl,
      CohortBuilderActionDialogType.SAVE_COHORT_DEFINITION,
      true
    ),
    countDown,
    onClose,
    onSubmit,
    redirectToCohortList,
    cohortData,
    submittingError,
    isSubmitting,
    isInitialLoading,
    initialLoadingError,
    isSubmitSuccess,
  };
};
